import type { Stack } from "../shared/stack.js";
import { stackDisplayName } from "../shared/stack.js";

const UNITS: Record<Stack["type"], string> = { item: "", fluid: " mB", essentia: "" };

export function compact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e12) return (n / 1e12).toFixed(abs >= 1e13 ? 0 : 1) + "T";
  if (abs >= 1e9) return (n / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + "G";
  if (abs >= 1e6) return (n / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + "M";
  if (abs >= 1e4) return (n / 1e3).toFixed(abs >= 1e5 ? 0 : 1) + "k";
  return String(Math.round(n));
}

export function amount(n: number | null, type: Stack["type"]): string {
  if (n === null) return "?";
  return compact(n) + UNITS[type];
}

export function full(n: number): string {
  return n.toLocaleString();
}

export function stackName(stack: Stack): string {
  return stackDisplayName(stack);
}

export function stackDetail(stack: Stack): string {
  return stack.type === "item" ? `${stack.name} : ${stack.damage ?? 0}` : `${stack.type} · ${stack.name}`;
}

export function ago(at: number | null): string {
  if (!at) return "never";
  const s = Math.round((Date.now() - at) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function time(at: number): string {
  const d = new Date(at);
  const today = new Date();
  return d.toDateString() === today.toDateString() ? d.toLocaleTimeString() : d.toLocaleString();
}

/** A length of time, in its two biggest units: "45s", "12m", "3h 20m", "4d 6h". */
export function duration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const hrs = Math.floor(m / 60);
  if (hrs < 24) return m % 60 ? `${hrs}h ${m % 60}m` : `${hrs}h`;
  const d = Math.floor(hrs / 24);
  if (d >= 365) return "over a year";
  return hrs % 24 ? `${d}d ${hrs % 24}h` : `${d}d`;
}

/** A per-hour rate with its sign, "+1.2k/h". Under 1 per hour shows one decimal. */
export function perHour(n: number, type: Stack["type"], signed = true): string {
  const sign = signed && n > 0.05 ? "+" : n < -0.05 ? "−" : "";
  const abs = Math.abs(n);
  const text = abs > 0 && abs < 10 ? abs.toFixed(1).replace(/\.0$/, "") : compact(abs);
  return `${sign}${text}${UNITS[type]}/h`;
}
